import React, { useEffect, useReducer, useRef, useState, memo } from "react";
import Carousel from "react-bootstrap/Carousel";
import { makeStyles } from "@material-ui/core/styles";
import AppBar from "@material-ui/core/AppBar";
import Button from "@material-ui/core/Button";
import ButtonBase from "@material-ui/core/ButtonBase";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import Divider from "@material-ui/core/Divider";
import Drawer from "@material-ui/core/Drawer";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import Hidden from "@material-ui/core/Hidden";
import IconButton from "@material-ui/core/IconButton";
import Link from "@material-ui/core/Link";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import ListItemText from "@material-ui/core/ListItemText";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AddIcon from "@material-ui/icons/Add";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import RemoveIcon from "@material-ui/icons/Remove";
import ShoppingCartOutlinedIcon from "@material-ui/icons/ShoppingCartOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import MailIcon from "@material-ui/icons/Mail";
import Alert from "@material-ui/lab/Alert";
import Currency from "./Currency";
import ImageLoader from "./ImageLoader";
import PoweredBy from "./PoweredBy";
import Product from "./Product";

const useStyles = makeStyles(theme => ({
  crossedPrice: {
    textDecoration: "line-through",
    marginLeft: theme.spacing(1)
  },
  sale: {
    padding: theme.spacing(1),
    display: "inline-block",
    position: "absolute",
    top: "0",
    right: "0",
    fontSize: "1rem",
    color: theme.palette.common.white,
    background: theme.palette.secondary.main,
    borderTopRightRadius: "5px",
    zIndex: 2
  },
  noStock: {
    height: "25px",

    "&:after": {
      content: '"Agotado"',
      padding: theme.spacing(1),
      fontSize: "1rem",
      color: theme.palette.primary.main,
      border: "1px solid",
      borderColor: theme.palette.primary.main,
      borderRadius: "3px"
    }
  }
}));

export const generateItemGroup = (productID, features) => {
  let group = productID;

  for (let i = 0; i < features.length; i++) {
    const feature = features[i];

    if (!feature.option) {
      group = null;
      break;
    } else {
      group += `[${feature.value}|${feature.option.value}]`;
    }
  }

  return group;
};

export const getSelectedFeatures = features => {
  const list = [];

  features.forEach(f => list.push({ value: f.value, label: f.label, option: f.option }));

  return list;
};

const initialState = {
  item: null,
  index: null,
  quantity: 0,
  features: []
};

const reducer = (state, { type, payload }) => {
  switch (type) {
    case "quantity":
    case "features":
      return { ...state, [type]: payload };
    case "item":
      return {
        ...state,
        item: payload.item,
        index: payload.index,
        quantity: payload.quantity
      };
    case "update":
      return {
        ...state,
        item: payload.item,
        quantity: payload.quantity
      };
    default:
      return { ...state };
  }
};

const AditionalFeaturesItem = memo(({ primary, secondary }) => {
  return (
    <ListItem>
      <ListItemText
        primary={primary}
        secondary={secondary}
        primaryTypographyProps={{
          component: "pre",
          variant: "body2"
        }}
        secondaryTypographyProps={{
          component: "pre",
          variant: "body2",
          color: "textSecondary"
        }}
      />
    </ListItem>
  );
});

export const AditionalFeatures = memo(({ product }) => {
  return (
    <Grid container>
      <Grid item xs={12}>
        <ListItem>
          <ListItemText
            primary="Características adicionales"
            primaryTypographyProps={{
              component: "pre",
              variant: "body1"
            }}
          />
        </ListItem>
      </Grid>

      {product.body.aditionalFeatures.sku && (
        <Grid item xs={4}>
          <AditionalFeaturesItem primary="SKU" secondary={product.body.aditionalFeatures.sku} />
        </Grid>
      )}

      {product.body.aditionalFeatures.weight && (
        <Grid item xs={4}>
          <AditionalFeaturesItem
            primary="Peso"
            secondary={`${product.body.aditionalFeatures.weight} Kg.`}
          />
        </Grid>
      )}

      {product.body.aditionalFeatures.height && (
        <Grid item xs={4}>
          <AditionalFeaturesItem
            primary="Alto"
            secondary={`${product.body.aditionalFeatures.height} Cm.`}
          />
        </Grid>
      )}

      {product.body.aditionalFeatures.width && (
        <Grid item xs={4}>
          <AditionalFeaturesItem
            primary="Ancho"
            secondary={`${product.body.aditionalFeatures.width} Cm.`}
          />
        </Grid>
      )}

      {product.body.aditionalFeatures.depth && (
        <Grid item xs={4}>
          <AditionalFeaturesItem
            primary="Largo"
            secondary={`${product.body.aditionalFeatures.depth} Cm.`}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <Divider />
      </Grid>
    </Grid>
  );
});

const ProductDetails = ({
  store,
  items,
  totalItems,
  totalPrice,
  product,
  cartOpen,
  open,
  onClose,
  onChange,
  onWhatsapp
}) => {
  const classes = useStyles();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [hideBlur, setHideBlur] = useState(false);
  const [stock, setStock] = useState(null);
  const isCartEmpty = useRef(totalItems === 0);

  useEffect(() => {
    let features = [];

    if (open && product) {
      if (product.body && product.body.features) {
        features = JSON.parse(JSON.stringify(product.body.features));

        const stock = Product.getStock(product, features);
        setStock(stock);
      }
    }

    dispatch({
      type: "features",
      payload: features
    });
  }, [open, product]);

  useEffect(() => {
    if (product) {
      let item = null;
      let index = null;
      let quantity = 0;

      const group = generateItemGroup(product.id, state.features);

      for (let i = 0; i < items.length; i++) {
        if (items[i].group === group) {
          item = items[i];
          index = i;
          quantity = items[i].quantity;
          break;
        }
      }

      if (!item) {
        const price =
          product.body.sale && product.body.sale.percentage
            ? product.body.sale.value
            : product.price;

        item = {
          productID: product.id,
          price,
          quantity,
          total: price * quantity,
          body: { features: state.features },
          group,
          product
        };
      }

      dispatch({
        type: "item",
        payload: {
          item,
          index,
          quantity
        }
      });
    }
  }, [items, product, state.features]);

  useEffect(() => {
    if (cartOpen) {
      setHideBlur(true);
    }
  }, [cartOpen]);

  const canSetQuantity = () => {
    if (state.features.length > 0) {
      for (let i = 0; i < state.features.length; i++) {
        if (!Boolean(state.features[i].option)) {
          return false;
        }
      }

      return true;
    } else {
      return true;
    }
  };

  const updateItem = quantity => {
    const selectedFeatures = getSelectedFeatures(state.features);

    const item = {
      ...state.item,
      quantity,
      total: state.item.price * quantity,
      body: { features: selectedFeatures },
      product
    };

    const group = generateItemGroup(product.id, selectedFeatures);

    if (group) {
      item.group = group;
    }

    const action = state.index === null ? "create" : quantity > 0 ? "update" : "delete";

    dispatch({
      type: "item",
      payload: {
        item,
        quantity
      }
    });

    onChange(action, item, state.index);
  };

  const updateQuantity = quantity => {
    if (stock) {
      if (quantity <= stock.quantity) {
        updateItem(quantity);
      }
    } else {
      updateItem(quantity);
    }
  };

  const addItem = () => {
    const quantity = state.quantity + 1;
    updateQuantity(quantity);
  };

  const removeItem = () => {
    const quantity = state.quantity - 1;
    updateQuantity(quantity);
  };

  const addButton = () => {
    if (stock && stock.quantity === 0) {
      return <div className={classes.noStock}></div>;
    }

    return (
      <ButtonGroup disabled={!canSetQuantity()}>
        <Button
          className="my-2"
          aria-label="reduce"
          size="small"
          disabled={state.quantity < 1}
          onClick={removeItem}
        >
          <RemoveIcon fontSize="small" />
        </Button>
        <Button className="my-2">{state.quantity}</Button>
        <Button className="my-2" aria-label="increase" size="small" onClick={addItem}>
          {stock && state.quantity >= stock.quantity ? "Máximo" : <AddIcon fontSize="small" />}
        </Button>
      </ButtonGroup>
    );
  };

  const selectFeature = (index, option) => {
    if (index >= 0) {
      const features = [...state.features];
      features[index].option = option;

      const stock = Product.getStock(product, features);
      setStock(stock);

      dispatch({
        type: "features",
        payload: features
      });
    }
  };

  const showBlur = () => {
    return isCartEmpty.current && state.quantity === 1 && !hideBlur;
  };

  if (!product) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        className: "full-width product-details"
      }}
      style={{ zIndex: 1298 }}
    >
      <Hidden mdUp>
        <AppBar color="transparent" position="fixed" elevation={0} className="app-top-bar gradient">
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={onClose}>
              <ArrowBackIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
      </Hidden>

      {showBlur() && (
        <div className="first-item-message" onClick={() => setHideBlur(true)}>
          <Alert severity="info">
            <span>
              Haz click aquí{" "}
              <span role="img" aria-label="point_down">
                👇
              </span>{" "}
              para ver tu carrito.
            </span>
          </Alert>
        </div>
      )}

      <Grid container className="h-100">
        <Grid item xs={12} sm={6} className={`${showBlur() && "blur"}`}>
          <Carousel interval={4000}>
            {product.medias &&
              product.medias.map((file, i) => (
                <Carousel.Item key={"archivo-" + i}>
                  <ImageLoader imgKey={file && file.key} className="h-100 w-100" />
                </Carousel.Item>
              ))}
          </Carousel>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Hidden smDown>
            <AppBar
              color="default"
              className={`app-top-bar w-50 ${showBlur() && "blur"}`}
              elevation={0}
            >
              <Toolbar>
                <IconButton className="mr-4" edge="start" color="inherit" onClick={onClose}>
                  <ArrowBackIcon />
                </IconButton>

                {store.logo ? (
                  <ImageLoader imgKey={store.logo.key} className="logo" circularProgressSize={20} />
                ) : (
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/images/avatar.png`}
                    className="logo"
                    alt="Logo"
                  />
                )}
                <Typography variant="h6" className="title">
                  {store.name}
                </Typography>
                {store.body.config && store.body.config.whatsapp && (
                  <IconButton
                    aria-label="Conversar con ejecutivo"
                    onClick={() =>
                      onWhatsapp(`Hola, tengo una duda sobre el producto "${product.name}"`)
                    }
                  >
                    <WhatsAppIcon />
                  </IconButton>
                )}
              </Toolbar>
            </AppBar>
            <div className="mb-3"></div>
          </Hidden>

          <List className={`details-list ${showBlur() && "blur"}`}>
            <ListItem>
              <ListItemText primary={<Typography variant="h6">{product.name}</Typography>} />
            </ListItem>

            <Divider />

            <ListItem>
              <ListItemText
                primary={
                  <Typography
                    component="pre"
                    variant="body2"
                    color="textSecondary"
                    className="description"
                  >
                    {product.body.description}
                  </Typography>
                }
              />
            </ListItem>

            <Divider />

            {product.body.aditionalFeatures && product.body.aditionalFeatures.status && (
              <AditionalFeatures product={product} />
            )}

            {state.features.map((feature, i) => (
              <div key={`feature-${i}`}>
                <Typography className="feature-name" variant="body1">
                  {feature.label}
                </Typography>
                <FormControl variant="outlined" className="feature-options" size="small">
                  <RadioGroup
                    aria-label={feature.label}
                    id={`feature-select-${i}`}
                    value={feature.option || ""}
                  >
                    {feature.options.map((option, j) => (
                      <FormControlLabel
                        key={`option-${i}-${j}`}
                        value={option}
                        control={<Radio />}
                        label={option.label}
                        onChange={() => selectFeature(i, option)}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>

                <Divider />
              </div>
            ))}

            <ListItem>
              <ListItemText
                primary={
                  <div className="d-flex align-items-center">
                    {product.body.sale && product.body.sale.percentage ? (
                      <>
                        <strong>
                          <Currency value={product.body.sale.value} />
                        </strong>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          className={classes.crossedPrice}
                        >
                          <Currency value={product.price} />
                        </Typography>
                      </>
                    ) : (
                      <strong>
                        <Currency value={product.price} />
                      </strong>
                    )}
                  </div>
                }
              />
              <ListItemSecondaryAction>{addButton()}</ListItemSecondaryAction>
            </ListItem>
            <Divider />

            <ListItem divider>
              <ListItemText className="text-right">
                {store.body.config.phone && (
                  <Link
                    href={`https://api.whatsapp.com/send?phone=569${store.body.config.phone}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <WhatsAppIcon fontSize="large" color="action" />
                  </Link>
                )}
                {store.body.config.email && (
                  <Link
                    href={`mailto:${store.body.config.email}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MailIcon fontSize="large" color="action" />
                  </Link>
                )}
              </ListItemText>
            </ListItem>

            {store.body.policies && (
              <>
                <ListItem>
                  <Typography color="textPrimary" variant="body1">
                    Políticas
                  </Typography>
                </ListItem>
                {store.body.policies && store.body.policies.cambios && (
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel1a-content"
                      id="panel1a-header"
                    >
                      <Typography variant="body1">Cambios</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography color="textSecondary" variant="body2">
                        {store.body.policies.cambios}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                )}
                {store.body.policies && store.body.policies.devoluciones && (
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel2a-content"
                      id="panel2a-header"
                    >
                      <Typography variant="body1">Devoluciones</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography color="textSecondary" variant="body2">
                        {store.body.policies.devoluciones}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                )}
              </>
            )}
            <PoweredBy />
          </List>

          <Hidden smDown>
            {items.length > 0 && (
              <AppBar color="default" className="app-bottom-bar w-50">
                <Toolbar className="toolbar-home">
                  <ButtonBase
                    disabled={totalItems < 1 || (stock && state.quantity > stock.quantity)}
                    onClick={() => {
                      setHideBlur(true);
                      onClose();
                    }}
                  >
                    <div className="toolbar-left ">
                      <ShoppingCartOutlinedIcon fontSize="large" className="mr-2" />
                      <Typography variant="h6">{totalItems}</Typography>
                    </div>
                    <Typography className="toolbar-right" variant="h6">
                      <Currency value={totalPrice} />
                    </Typography>
                  </ButtonBase>
                </Toolbar>
              </AppBar>
            )}
          </Hidden>
        </Grid>
      </Grid>
    </Drawer>
  );
};

export default ProductDetails;
